import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { LoanOfficer } from '../data/mockData';
import { formatCurrency, formatPercent, formatMinutes, getPerformanceColor } from '../utils/helpers';
import { DateRange, isCountMode } from './DashboardHeader';
interface LoanOfficerCardsProps {
  loanOfficers: LoanOfficer[];
  onSelectLO: (lo: LoanOfficer) => void;
  dateRange: DateRange;
  visibleColumns: string[];
}
export function LoanOfficerCards({
  loanOfficers,
  onSelectLO,
  dateRange,
  visibleColumns
}: LoanOfficerCardsProps) {
  const showCounts = isCountMode(dateRange);
  const isVisible = (col: string) => visibleColumns.includes(col);
  const getStatusBadge = (lo: LoanOfficer) => {
    if (lo.coachingFlags.length > 0) {
      return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-126">
          Needs Coaching
        </span>;
    }
    const yellowCount = [getPerformanceColor('totalCalls', lo.totalCalls), getPerformanceColor('transferRate', lo.transferRate), getPerformanceColor('failedTransferPct', lo.failedTransferPct), getPerformanceColor('activePipelineValue', lo.activePipelineValue)].filter(c => c === 'yellow').length;
    if (yellowCount >= 2) {
      return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-127">
          Warning
        </span>;
    }
    return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-128">
        Strong
      </span>;
  };
  const MetricItem = ({
    label,
    value,
    metricKey,
    isPercent = false,
    isCurrency = false,
    isTime = false
  }: {
    label: string;
    value: number;
    metricKey: string;
    isPercent?: boolean;
    isCurrency?: boolean;
    isTime?: boolean;
  }) => {
    const color = getPerformanceColor(metricKey, value);
    const dotColor = color === 'green' ? 'bg-green-500' : color === 'yellow' ? 'bg-amber-500' : 'bg-red-500';
    let displayValue = value.toString();
    if (isPercent) displayValue = formatPercent(value);
    if (isCurrency) displayValue = formatCurrency(value);
    if (isTime) displayValue = formatMinutes(value);
    return <div className="flex justify-between items-center py-1" data-id="element-129">
        <span className="text-xs text-gray-500" data-id="element-130">{label}</span>
        <div className="flex items-center gap-1.5" data-id="element-131">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} data-id="element-132"></span>
          <span className="text-sm font-medium text-gray-900" data-id="element-133">
            {displayValue}
          </span>
        </div>
      </div>;
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-134">
      {loanOfficers.map(lo => {
      const isFlagged = lo.coachingFlags.length > 0;
      return <div key={lo.id} onClick={() => onSelectLO(lo)} className={`bg-white rounded-xl shadow-sm p-5 cursor-pointer transition-all hover:shadow-md border border-gray-100 ${isFlagged ? 'border-l-4 border-l-red-500' : ''}`} data-id="element-135">
            <div className="flex justify-between items-start mb-4" data-id="element-136">
              <div className="flex items-center gap-3" data-id="element-137">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold" data-id="element-138">
                  {lo.initials}
                </div>
                <div data-id="element-139">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1" data-id="element-140">
                    {lo.name}
                    {isFlagged && <div className="relative group/flag flex items-center" data-id="element-141">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500 cursor-help" data-id="element-142" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover/flag:block w-72 max-w-sm bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-10 font-normal" data-id="element-143">
                          <p className="font-bold mb-1 border-b border-gray-700 pb-1" data-id="element-144">
                            Coaching Flags
                          </p>
                          <ul className="list-disc pl-4 space-y-1" data-id="element-145">
                            {lo.coachingFlags.map(f => <li key={f.id} data-id="element-146">{f.description}</li>)}
                          </ul>
                          <div className="absolute left-3 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" data-id="element-147"></div>
                        </div>
                      </div>}
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-148">
                    {lo.team} • {lo.roleStage}
                  </p>
                </div>
              </div>
              {getStatusBadge(lo)}
            </div>

            <div className="mb-4" data-id="element-149">
              <p className="text-xs text-gray-500 mb-1" data-id="element-150">Active Pipeline $</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-151">
                {formatCurrency(lo.activePipelineValue)}
              </p>
            </div>

            <div className="space-y-1 bg-gray-50 p-3 rounded-lg" data-id="element-152">
              {isVisible('totalCalls') && <MetricItem label="Total Calls" value={lo.totalCalls} metricKey="totalCalls" data-id="element-153" />}
              {isVisible('totalEngagedCalls') && <MetricItem label="Total Engaged Calls" value={lo.totalEngagedCalls} metricKey="totalEngagedCalls" data-id="element-154" />}
              {isVisible('conversations60Plus') && <MetricItem label="Conversations 60+s" value={lo.conversations60Plus} metricKey="conversations60Plus" data-id="element-155" />}
              {isVisible('totalTalkTime') && <MetricItem label="Total Talk Time" value={lo.totalTalkTime} metricKey="totalTalkTime" isTime data-id="element-156" />}
              {isVisible('totalTransfers') && <MetricItem label="Total Transfers" value={lo.totalTransfers} metricKey="totalTransfers" data-id="element-157" />}
              {isVisible('qualifiedTransferPct') && <MetricItem label="Qualified Transfer %" value={lo.qualifiedTransferPct} metricKey="qualifiedTransferPct" isPercent data-id="element-158" />}
              {isVisible('transferRate') && <MetricItem label="Transfer Rate" value={lo.transferRate} metricKey="transferRate" isPercent data-id="element-159" />}
              {isVisible('failedTransferPct') && <MetricItem label="Failed Transfer %" value={lo.failedTransferPct} metricKey="failedTransferPct" isPercent data-id="element-160" />}
              {isVisible('transferToSubmissionPct') && <MetricItem label="Transfer → Sub %" value={lo.transferToSubmissionPct} metricKey="transferToSubmissionPct" isPercent data-id="element-161" />}
              {isVisible('docsCollectedPct') && <MetricItem label="Docs Collected %" value={lo.docsCollectedPct} metricKey="docsCollectedPct" isPercent data-id="element-162" />}
            </div>
          </div>;
    })}
    </div>;
}